import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { WalletBrickCTA } from './WalletBrickCTA';
const BACKEND_URL = 'https://mercadopago-her-backend.vercel.app';
const getCookie = (name: string) => {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : undefined;
};

// Validación nativa del navegador — más robusta que regex custom
// Usa el motor de validación de type="email" del browser
const checkEmail = (value: string): boolean => {
  if (!value || value.length < 6) return false;
  const input = document.createElement('input');
  input.type = 'email';
  input.value = value;
  return input.checkValidity();
};
export function OfferSection() {
  const [email, setEmail] = useState('');
  const [step, setStep] = useState<'email' | 'wallet'>('email');
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isValid = checkEmail(email);

  // Pre-cargar el SDK de MP cuando la sección monta
  useEffect(() => {
    if ((window as any).MercadoPago) return;
    if (document.querySelector('script[src="https://sdk.mercadopago.com/js/v2"]')) return;
    const s = document.createElement('script');
    s.src = 'https://sdk.mercadopago.com/js/v2';
    s.async = true;
    document.head.appendChild(s);
  }, []);

  // Pre-rellenar desde localStorage para usuarios que vuelven
  useEffect(() => {
    try {
      const saved = localStorage.getItem('her_email');
      if (saved && checkEmail(saved)) setEmail(saved);
    } catch {}
  }, []);
  const handleEmailSubmit = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!isValid || submitting) return;
    setSubmitting(true);
    try {
      localStorage.setItem('her_email', email);
    } catch {}

    // Fire-and-forget — no bloquea la transición al Wallet Brick
    fetch(`${BACKEND_URL}/api/capture-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email,
        // UUID generado en el servidor para deduplicación
        // No generamos el event_id acá para no tener dos IDs distintos
        fbp: getCookie('_fbp'),
        fbc: getCookie('_fbc'),
        source: 'her-landing-offer'
      })
    }).catch(() => {});
    try {
      ;
      (window as any).gtag?.('event', 'email_submitted', {
        email_domain: email.split('@')[1]
      });
    } catch {}
    setSubmitting(false);
    setStep('wallet');
  }, [email, isValid, submitting]);
  const handleChangeEmail = () => {
    setStep('email');
    setTimeout(() => inputRef.current?.focus(), 100);
  };
  return <section id="offer" className="bg-gradient-hero py-20 md:py-28 px-4 md:px-8 relative overflow-hidden" style={{
    paddingTop: "0px"
  }} data-id="element-221" data-mp="364ydu52j29b0">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gold/[0.06] blur-[120px] rounded-full pointer-events-none" data-id="element-222" data-mp="2x3qssop0v4yv" />

      <div className="max-w-md mx-auto relative z-10" data-id="element-223" data-mp="2di732zuh40yc">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="card-premium !border-gold/25 px-7 py-12 md:px-10 md:py-14 shadow-gold text-center space-y-8" data-id="element-224" data-mp="28jdm2xfz9rtf">
          <p className="uppercase tracking-[0.28em] font-semibold text-[#C8A870] text-[14px]" data-id="element-225" data-mp="2wfrgnarsfd3n">
            Acceso completo al Protocolo H.E.R.
          </p>

          <p className="text-foreground/35 font-body font-medium line-through decoration-foreground/20 decoration-1 text-[20px]" data-id="element-226" data-mp="2rfvktyt8urz6">
            $44.000 ARS
          </p>

          <p className="uppercase tracking-[0.24em] text-foreground/60 font-semibold -mt-2 text-[14px]" data-id="element-227" data-mp="3181c16bo95db">
            Hoy
          </p>

          <div className="space-y-2" data-id="element-228" data-mp="3a87qovj3wbe2">
            <p className="text-6xl md:text-7xl font-display font-semibold text-gradient-gold leading-none" data-id="element-229" data-mp="3a98zetcnglsm">
              $12.000
            </p>
            <p className="font-body text-[14px] text-foreground/50 tracking-wide" data-id="element-230" data-mp="18h7hlantshj8">
              ARS · pago único
            </p>
          </div>

          <p className="font-body md:text-[16px] text-[#C8A870] text-[17px]" data-id="element-231" data-mp="1i9wft3ud7ywm">
            O en <span className="font-semibold" data-id="element-232" data-mp="1mayffls72cz4">3 cuotas de $4.000</span> con Mercado Pago
          </p>

          <p className="font-display italic md:text-[18px] max-w-xs mx-auto text-[#F0E8D8] text-[18px]" data-id="element-233" data-mp="pkpg41j6d39s">
            Empezá hoy. En 21 días vas a haber recorrido H.E.R. completo.
          </p>

          <p className="font-body tracking-wide text-[#C8A870] text-[14px]" data-id="element-234" data-mp="ykclszixsv8n">
            📩 Acceso inmediato por email apenas se aprueba el pago.
          </p>

          {/* ===== CHECKOUT EN DOS PASOS ===== */}
          <div className="pt-2 space-y-4" data-id="element-235" data-mp="2a0sr400s1vhs">
            <AnimatePresence mode="wait" data-id="element-236" data-mp="2a23wg49vfbb0">

              {step === 'email' && <motion.form key="email-step" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -8
            }} transition={{
              duration: 0.3
            }} onSubmit={handleEmailSubmit} className="space-y-4" noValidate data-id="element-237" data-mp="2ey2teg99m1n1">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#F0E8D8]/45 text-left" style={{
                fontFamily: 'sans-serif'
              }} data-id="element-238" data-mp="2bbmg9howozj9">
                    Ingresá tu email para continuar
                  </p>

                  <div className="relative" data-id="element-239" data-mp="21cf0yoym2uti">
                    <input ref={inputRef} type="email" name="email" autoComplete="email" inputMode="email" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleEmailSubmit()} placeholder="tu@email.com" className="w-full bg-transparent py-3 text-[#F0E8D8] placeholder-[#F0E8D8]/25 focus:outline-none text-[16px] font-body" style={{
                  borderBottom: `1px solid ${isValid ? 'rgba(200,168,112,0.6)' : 'rgba(240,232,216,0.15)'}`,
                  transition: 'border-color 0.3s ease'
                }} data-id="element-240" data-mp="21ft5o3uqzg44" />
                    {isValid && <motion.span initial={{
                  opacity: 0,
                  scale: 0.6
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} className="absolute right-0 top-3 text-[#C8A870] text-[16px] select-none" data-id="element-241" data-mp="1ovmcxuphwhbz">
                        ✔
                      </motion.span>}
                  </div>

                  <button type="submit" disabled={!isValid || submitting} className="w-full py-4 font-sans font-bold uppercase tracking-[0.12em] text-[13px] rounded-[3px] transition-all duration-200" style={{
                background: isValid ? '#C8A870' : 'rgba(200,168,112,0.2)',
                color: isValid ? '#0a0807' : 'rgba(200,168,112,0.4)',
                cursor: isValid ? 'pointer' : 'not-allowed'
              }} data-id="element-242" data-mp="27tgoafe3j4xj">
                    {submitting ? 'Un momento…' : 'Ver opciones de pago →'}
                  </button>

                  <p className="text-[11px] text-[#F0E8D8]/30 leading-[1.5]" style={{
                fontFamily: 'sans-serif'
              }} data-id="element-243" data-mp="evtd6avql2vn">
                    Solo usamos tu email para enviarte el acceso. Sin spam.
                  </p>
                </motion.form>}

              {step === 'wallet' && <motion.div key="wallet-step" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -8
            }} transition={{
              duration: 0.35
            }} className="space-y-4" data-id="element-244" data-mp="jvo3odblnqg6">
                  <div className="flex items-center justify-between px-3 py-2 rounded-[4px]" style={{
                background: 'rgba(200,168,112,0.06)',
                border: '0.5px solid rgba(200,168,112,0.18)'
              }} data-id="element-245" data-mp="5wy9p6k2u6w1">
                    <span className="text-[13px] text-[#F0E8D8]/70 font-body truncate mr-2" data-id="element-246" data-mp="39j6slugq8qef">
                      ✔︎  {email}
                    </span>
                    <button onClick={handleChangeEmail} className="text-[11px] text-[#C8A870]/60 hover:text-[#C8A870] flex-shrink-0 transition-colors" style={{
                  fontFamily: 'sans-serif',
                  letterSpacing: '0.06em'
                }} data-id="element-247" data-mp="2j3wzd6a6wd3h">
                      Cambiar
                    </button>
                  </div>

                  <WalletBrickCTA email={email} data-id="element-248" data-mp="2xy1hgo767uxo" />
                </motion.div>}

            </AnimatePresence>
          </div>

          

          <div className="space-y-3 pt-4" data-id="element-249" data-mp="370apn7eyvc7w">
            <p className="uppercase tracking-[0.22em] text-[#C8A870] font-semibold text-[14px]" data-id="element-250" data-mp="36z7xyx9tl5qx">
              ✦ Garantía de 30 días
            </p>
            <p className="font-body md:text-[15px] max-w-sm mx-auto text-[#F0E8D8] text-[15px]" data-id="element-251" data-mp="3gqccjvvl2ol8">
              Probálo durante 30 días. Si H.E.R. no es para vos, te devolvemos el 100% de tu dinero.
            </p>
          </div>

          <img src="https://cdn.magicpatterns.com/uploads/1a9ZqWHLFGeJEBrZWczanr/mp.webp" alt="Pago seguro con Mercado Pago" loading="lazy" decoding="async" style={{
          width: '100%',
          maxWidth: 430,
          height: 'auto',
          display: 'block',
          margin: '0 auto'
        }} data-id="element-252" data-mp="3psbucqef9wp8" />
        </motion.div>
      </div>
    </section>;
}